import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Input, Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import WorkingWithModal from "../../WorkingWithModal";
import { connect } from "react-redux";
import { VirtuosoGrid } from "react-virtuoso";
import debounce from "lodash.debounce";

import {
  getMatches as getMatchesAction,
  getDataToFilter as getDataToFilterAction,
  convertRetailerToBrand as convertRetailerToBrandAction,
  convertBrandToRetailer as convertBrandToRetailerAction,
  bulkDeleteMatches as bulkDeleteMatchesAction,
  fetchMatchesLocations as fetchMatchesLocationsAction,
} from "../../../../ReduxToolkit/Reducers/matches";
import ProfileBrandMatchesEmptyState from "./ProfileBrandMatchesEmptyState";
import ProfileInfoBrandTabs from "./ProfileInfoBrandTabs";
import ProfileInfoItem from "./ProfileInfoItem";
import ProfileInfoLoader from "./ProfileInfoLoader";
import { useParams } from "react-router-dom";
import EditRetailerMatchingModal from "../../EditRetailerMatchingModal";
import EditBrandMatchingModal from "../../EditBrandMatchingModal";
import { Edit2, Trash2, MapPin } from "react-feather";
import MultiSelectFilterDropDown from "../../../../CommonElements/MultiSelectFilterDropDown";

const TAKE = 40;

// Maps UI tab label → API tab value + matched_by_ai flag
const getApiTabParams = (selectedTab) => {
  switch (selectedTab) {
    case "Already Working With":
      return { tab: "matches", matched_by_ai: false };
    case "Matched By AI":
      return { tab: "matches", matched_by_ai: true };
    case "Next To Brands":
      return { tab: "next_to" };
    case "Competitors":
      return { tab: "competitor" };
    default:
      return null;
  }
};

const GridList = React.forwardRef(({ style, children, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(max(260px, calc(25% - 1rem)), 1fr))",
      gap: "1rem",
      ...style,
    }}
  >
    {children}
  </div>
));
GridList.displayName = "GridList";

const GridItem = ({ children, ...props }) => <div {...props}>{children}</div>;

const ProfileInfoSection = ({
  brand,
  matchLoading,
  filterLoading,
  matchItems,
  matchTotal,
  filterData,
  getMatches,
  getDataToFilter,
  convertRetailerToBrand,
  convertBrandToRetailer,
  bulkDeleteMatches,
  fetchMatchesLocations,
}) => {
  const { type } = useParams();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [workingWithModal, setWorkingWithModal] = useState(null);
  const [selectedTab, setSelectedTab] = useState("Already Working With");
  const [searchTxt, setSearchTxt] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFetchingLocations, setIsFetchingLocations] = useState(false);
  const skipRef = useRef(0);

  const isBrand = type === "brand";
  const isComingSoon = selectedTab === "Similar Retailers";

  const tabParams = useMemo(() => getApiTabParams(selectedTab), [selectedTab]);

  // Check if any filters are applied
  const hasFiltersApplied = useMemo(() => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => Array.isArray(v) && v.length > 0)
    );
    return Object.keys(cleanFilters).length > 0 || debouncedQuery.trim();
  }, [filters, debouncedQuery]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(debounce((val) => setDebouncedQuery(val), 800), []);

  const buildMatchPayload = useCallback(
    (skip, append) => {
      if (!brand?.id || !tabParams) return null;

      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => Array.isArray(v) && v.length > 0)
      );

      return {
        id: brand.id,
        entity_type: type,
        skip,
        take: TAKE,
        ...tabParams,
        ...(debouncedQuery && { query: debouncedQuery }),
        ...(Object.keys(cleanFilters).length > 0 && { filters: cleanFilters }),
        append,
      };
    },
    [brand?.id, type, tabParams, debouncedQuery, filters]
  );

  // Load filter options when tab or brand changes
  useEffect(() => {
    if (!brand?.id || isComingSoon || !tabParams) return;
    getDataToFilter({ id: brand.id, tab: tabParams.tab, entity_type: type, matched_by_ai: tabParams.matched_by_ai });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, brand?.id]);

  // Load matches fresh when tab / brand / filters / search changes
  useEffect(() => {
    if (!brand?.id || isComingSoon || !tabParams) return;
    skipRef.current = 0;
    const payload = buildMatchPayload(0, false);
    if (payload) getMatches(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, brand?.id, filters, debouncedQuery]);

  const loadMore = useCallback(() => {
    if (matchLoading || matchItems.length >= matchTotal || !brand?.id || !tabParams) return;
    const newSkip = skipRef.current + TAKE;
    skipRef.current = newSkip;
    const payload = buildMatchPayload(newSkip, true);
    if (payload) getMatches(payload);
  }, [matchLoading, matchItems.length, matchTotal, brand?.id, tabParams, buildMatchPayload, getMatches]);

  const handleEditSuccess = useCallback(() => {
    skipRef.current = 0;
    const payload = buildMatchPayload(0, false);
    if (payload) getMatches(payload);
  }, [buildMatchPayload, getMatches]);

  const handleConvert = useCallback(async (item) => {
    let result;
    if (item?.type === "retailer") {
      result = await convertRetailerToBrand(item.id);
    } else {
      result = await convertBrandToRetailer(item.id);
    }
    if (result?.statusCode === 200) {
      skipRef.current = 0;
      const payload = buildMatchPayload(0, false);
      if (payload) getMatches(payload);
    }
  }, [convertRetailerToBrand, convertBrandToRetailer, buildMatchPayload, getMatches]);

  const handleTabSelect = (tab) => {
    setSelectedTab(tab);
    setFilters({});
    setSearchTxt("");
    setDebouncedQuery("");
  };

  const handleSearchChange = (e) => {
    setSearchTxt(e.target.value);
    debounceFn(e.target.value);
  };

  const _toggleEditModal = () => setEditModalOpen((prev) => !prev);

  const handleWorkingWithClick = useCallback((entry, parentItem) => {
    setWorkingWithModal({ entry, parentItem });
  }, []);

  const handleBulkDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (!brand?.id || !tabParams || isDeleting) return;

    setIsDeleting(true);

    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => Array.isArray(v) && v.length > 0)
      );

      const payload = {
        id: brand.id,
        entity_type: type,
        ...tabParams,
        ...(debouncedQuery && { query: debouncedQuery }),
        ...(Object.keys(cleanFilters).length > 0 && { filters: cleanFilters }),
      };

      const result = await bulkDeleteMatches(payload);

      if (result?.statusCode === 200) {
        // Reset filters and refresh the matches after successful deletion
        setShowDeleteConfirmation(false);
        setFilters({});
        setSearchTxt("");
        setDebouncedQuery("");
        skipRef.current = 0;
        const refreshPayload = buildMatchPayload(0, false);
        if (refreshPayload) getMatches({ ...refreshPayload, filters: {} });
      }
    } catch (error) {
      console.error('Error deleting matches:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFetchMatchesLocations = async () => {
    if (!brand?.id || isFetchingLocations) return;

    setIsFetchingLocations(true);

    try {
      await fetchMatchesLocations({
        id: brand.id,
        tab: tabParams?.tab,
      });
    } finally {
      setIsFetchingLocations(false);
    }
  };

  const GridFooter = useCallback(
    () =>
      matchLoading ? (
        <div className="d-flex justify-content-center py-3">
          <div className="spinner-border spinner-border-sm text-secondary" role="status" />
        </div>
      ) : matchItems.length < matchTotal ? (
        <div className="d-flex justify-content-center py-2">
          <small className="text-muted">Scroll to load more</small>
        </div>
      ) : null,
    [matchLoading, matchItems.length, matchTotal]
  );

  return (
    <Card className="p-4 my-3 shadow-sm rounded">


      <ProfileInfoBrandTabs
        selectedTab={selectedTab}
        isLoading={matchLoading}
        isBrand={isBrand}
        onTabSelect={handleTabSelect}
      />

      {!isComingSoon && (
        <div className="d-flex justify-content-between mb-3 gap-2 ">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
            <button
              onClick={_toggleEditModal}
              disabled={matchLoading}
              className="btn btn-outline-primary d-flex align-items-center gap-2"
            >
              <Edit2 size={20} />
              Edit
            </button>

            {["Matched By AI", "Next To Brands", "Competitors"].includes(selectedTab) && isBrand && (
              <button
                onClick={handleFetchMatchesLocations}
                disabled={matchLoading || isFetchingLocations}
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
              >
                {isFetchingLocations ? (
                  <span className="spinner-border spinner-border-sm" role="status" />
                ) : (
                  <MapPin size={18} />
                )}
                Fetch Matches Location
              </button>
            )}

            <p className="mb-0"><span>Total:</span> <span className="fw-bold">{matchTotal}</span> Results</p>
          </div>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
            <div className="d-flex align-items-center gap-2">
              <MultiSelectFilterDropDown
                data={filterData}
                filters={filters}
                isLoading={filterLoading}
                onFilterApply={setFilters}
              />
              {hasFiltersApplied && (
                <button
                  onClick={handleBulkDeleteClick}
                  disabled={matchLoading || isDeleting}
                  className="btn btn-outline-danger d-flex align-items-center gap-2"
                  title="Remove all filtered matches"
                >
                  <Trash2 size={16} />
                  Remove Bulk Matches
                </button>
              )}
            </div>
            <div className="d-flex align-items-center gap-2 ms-2">
              <Input
                type="text"
                placeholder="Search"
                value={searchTxt}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>
      )}

      {isComingSoon ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <h4 className="text-muted">Coming Soon</h4>
        </div>
      ) : matchLoading && matchItems.length === 0 ? (
        <ProfileInfoLoader />
      ) : matchItems.length > 0 ? (
        <VirtuosoGrid
          style={{ height: "600px" }}
          totalCount={matchItems.length}
          endReached={loadMore}
          components={{
            List: GridList,
            Item: GridItem,
            Footer: GridFooter,
          }}
          itemContent={(index) => (
            <ProfileInfoItem
              item={matchItems[index]}
              selectedTab={selectedTab}
              navigateToBrand={true}
              onConvert={handleConvert}
              onWorkingWithClick={handleWorkingWithClick}
              onDeleteSuccess={handleEditSuccess}
            />
          )}
        />
      ) : (
        <ProfileBrandMatchesEmptyState
          match_status={brand?.match_status}
          readyToMatch={brand?.ready_to_match}
          isBrand={isBrand}
          selectedTab={selectedTab}
          searchTxt={searchTxt}
        />
      )}

      {editModalOpen && (
        <>
          {(selectedTab === "Matched By AI" || selectedTab === "Already Working With") &&
            type === "brand" && (
              <EditBrandMatchingModal
                open={editModalOpen}
                selectedTab={selectedTab}
                onClose={_toggleEditModal}
                onSuccess={handleEditSuccess}
                loadMore={loadMore}
              />
            )}
          {(selectedTab === "Matched By AI" || selectedTab === "Already Working With") &&
            type === "retailer" && (
              <EditRetailerMatchingModal
                open={editModalOpen}
                selectedTab={selectedTab}
                onClose={_toggleEditModal}
                onSuccess={handleEditSuccess}
                loadMore={loadMore}
              />
            )}
          {(selectedTab === "Next To Brands" || selectedTab === "Competitors") && (
            <EditRetailerMatchingModal
              open={editModalOpen}
              selectedTab={selectedTab}
              onClose={_toggleEditModal}
              onSuccess={handleEditSuccess}
              loadMore={loadMore}
            />
          )}
        </>
      )}
      <WorkingWithModal
        open={Boolean(workingWithModal)}
        onClose={() => setWorkingWithModal(null)}
        matchItem={workingWithModal?.parentItem}
      />

      {/* Bulk Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirmation} toggle={() => setShowDeleteConfirmation(false)}>
        <ModalHeader toggle={() => setShowDeleteConfirmation(false)}>
          Confirm Bulk Delete
        </ModalHeader>
        <ModalBody>
          <div className="d-flex align-items-start gap-3">
            <div className="text-warning">
              <i className="fas fa-exclamation-triangle fa-2x"></i>
            </div>
            <div>
              <h6 className="text-danger">Warning: This action cannot be undone!</h6>
              <p className="mb-2">
                You are about to permanently delete <strong>all filtered matches</strong> for this {type}.
              </p>
              <p className="mb-2">
                This will delete approximately <strong>{matchTotal} matches</strong> based on your current filters.
              </p>
              <p className="text-muted small">
                Applied filters: {hasFiltersApplied ? 'Yes' : 'None'}
                {debouncedQuery && ` | Search: "${debouncedQuery}"`}
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowDeleteConfirmation(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleConfirmBulkDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Deleting...
              </>
            ) : (
              'Yes, Delete All Matches'
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  matchLoading: state.matches.matchLoading,
  filterLoading: state.matches.filterLoading,
  brand: state.brands.selectedBrand,
  matchItems: state.matches.matchItems,
  matchTotal: state.matches.matchTotal,
  filterData: state.matches.filterData,
});

const mapDispatchToProps = {
  getMatches: getMatchesAction,
  getDataToFilter: getDataToFilterAction,
  convertRetailerToBrand: convertRetailerToBrandAction,
  convertBrandToRetailer: convertBrandToRetailerAction,
  bulkDeleteMatches: bulkDeleteMatchesAction,
  fetchMatchesLocations: fetchMatchesLocationsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileInfoSection);
